import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AssetDto, GalleryDto, Section, SectionObjectType, TenantDto } from '@nusign/picflow-backend-types';
import { Subject, filter, firstValueFrom, switchMap, take, takeUntil } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { Rect } from '../../../classes/rect.class';
import { IsDarkColorPipe } from '../../../color-picker/is-dark-color.pipe';
import { dragScrollThreshold } from '../../../consts';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { loadThemeFonts } from '../../../gallery-shared/lib/load-theme-fonts';
import { GROWTHBOOK } from '../../../growthbook';
import { calcGalleryPermissions } from '../../../lib/access-level-helpers';
import { getGalleryBackLink } from '../../../lib/get-gallery-back-link';
import { sleep } from '../../../lib/sleep';
import { testImageUrl } from '../../../lib/test-image-url';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { PICFLOW_CHECKOUT } from '../../../picflow-checkout';
import { AppConfigService } from '../../../providers/config.service';
import { AiService } from '../../../services/ai.service';
import { AssetApprovalLimitService } from '../../../services/asset-approval-limit.service';
import { AssetMoveService } from '../../../services/asset-move.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { FolderService } from '../../../services/folder.service';
import { GalleryAccessLevelService } from '../../../services/gallery-access-level.service';
import { GalleryService } from '../../../services/gallery.service';
import { NetworkService } from '../../../services/network.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { SentryService } from '../../../services/sentry.service';
import { StateService } from '../../../services/state.service';
import { StorageService } from '../../../services/storage.service';
import { SortByPipe } from '../../../shared/pipes/sort-by.pipe';
import { GalleryItem } from '../../classes/gallery-item.class';
import { AddSectionDialogComponent } from '../../dialogs/add-section-dialog/add-section-dialog.component';
import { ManualConfirmDialogComponent } from '../../dialogs/manual-confirm-dialog/manual-confirm-dialog.component';
import { calcLegacyTheme } from '../../lib/calc-legacy-theme';
import { AssetsSectionComponent } from '../assets-section/assets-section.component';
import { EditAlbumSectionComponent } from '../edit-album-section/edit-album-section.component';
import { EditEmbedSectionComponent } from '../edit-embed-section/edit-embed-section.component';
import { EditTextSectionComponent } from '../edit-text-section/edit-text-section.component';
import { GalleryCoverComponent } from '../gallery-cover/gallery-cover.component';
import { ImageGeneratorPromptComponent } from '../image-generator-prompt/image-generator-prompt.component';
@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GalleryCoverComponent,
    AssetsSectionComponent,
    EditTextSectionComponent,
    EditAlbumSectionComponent,
    EditEmbedSectionComponent,
    SortByPipe,
    IsDarkColorPipe,
    AsyncPipe,
    ImageGeneratorPromptComponent,
  ],
})
export class GalleryComponent implements OnInit, OnDestroy {
  public growthBook = inject(GROWTHBOOK);
  private assetService = inject(AssetService);
  private state = inject(StateService);
  private sectionService = inject(SectionService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private router = inject(Router);
  private galleryService = inject(GalleryService);
  private permissionService = inject(PermissionService);
  private assetMoveService = inject(AssetMoveService);
  private accessLevelService = inject(GalleryAccessLevelService);
  private sentryService = inject(SentryService);
  private dialog = inject(DialogService);
  private folderService = inject(FolderService);
  private config = inject(AppConfigService);
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private check = inject(ChangeDetectorRef);
  private checkoutService = inject(PICFLOW_CHECKOUT);
  private networkService = inject(NetworkService);
  public aiService = inject(AiService);
  private storage = inject(StorageService);
  gallery = input<GalleryDto>(undefined);
  tenantId = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    if (typeof gallery.tenant === 'string') return gallery.tenant;
    return gallery.tenant.id;
  });
  isTeamMember = computed(() => {
    const tenantId = this.tenantId();
    if (!tenantId) return;
    return this.state.tenants?.some(t => t.id === tenantId);
  });
  readonly sections = input<Section[]>(undefined);
  noBottomPadding = input<boolean>(undefined);
  @Input() filters: Filter[] = [];
  @ViewChildren(AssetsSectionComponent) assetSections: QueryList<AssetsSectionComponent>;
  @ViewChildren(AssetsSectionComponent, { read: ElementRef }) assetSectionHtmls: QueryList<ElementRef>;
  @ViewChildren(EditAlbumSectionComponent) albumSections: QueryList<EditAlbumSectionComponent>;
  @ViewChildren(EditAlbumSectionComponent, { read: ElementRef }) albumSectionHtmls: QueryList<ElementRef>;
  @ViewChild('galleryCover') cover: GalleryCoverComponent;
  @ViewChild('galleryCover', { read: ElementRef, static: true }) coverEl: ElementRef<HTMLElement>;
  scrollingPage = 0;
  startDragSection: AssetsSectionComponent;
  startDragPosition: number;
  draggingItem: GalleryItem;
  currentDragSection: AssetsSectionComponent | EditAlbumSectionComponent;
  currentDragGallery: GalleryDto;
  draggingGalleryItem: GalleryDto;
  colorPickerOpen = false;
  private accessLevel = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(gallery => this.accessLevelService.depricatedGetAccessLevel(gallery))
    )
  );
  public galleryPermissions = computed(() => calcGalleryPermissions(this.gallery(), this.accessLevel()));

  allowedAndEnabledFeedbackOptions = computed(() => {
    const gallery = this.gallery();
    const galleryPermissions = this.galleryPermissions();
    if (!gallery || !galleryPermissions) return;
    return this.permissionService.getAllowedAndEnabledFeedbackOptions(
      gallery,
      galleryPermissions.can_review_if_authenticated
    );
  });
  approvedAssetCount = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(gallery => this.assetApprovalLimitService.getApprovedAssetCountForGallery(gallery.id))
    )
  );
  theme = computed(() => {
    if (!this.gallery()) return;
    return this.gallery().theme || calcLegacyTheme(this.gallery());
  });

  destroyed$ = new Subject<void>();
  recordedImgErrorCount = 0;

  private checkImageError(err: ErrorEvent) {
    const tagName = (err.target as HTMLElement)?.tagName;
    if (!tagName || tagName !== 'IMG') return;
    const img = err.target as HTMLImageElement;
    if (!img.src.includes(this.config.assetsEndpoint)) return;
    this.recordedImgErrorCount += 1;
    if (this.recordedImgErrorCount > 5) return;
    this.networkService.isCurrentlyOnline().subscribe(async online => {
      if (!online) return;
      if (await testImageUrl(img.src)) return;
      console.warn('Error loading image', img.src);
      this.sentryService.captureException('img-loading-error');
    });
  }
  boundCheckImageError = this.checkImageError.bind(this);

  constructor() {
    effect(() => {
      const sections = this.sections();
      const galleryPermissions = this.galleryPermissions();
      if (!sections || !galleryPermissions) return;
      this.addEmptySectionIfNeeded();
    });

    effect(() => {
      const theme = this.theme();
      const gallery = this.gallery();
      if (!theme || !gallery) return;
      loadThemeFonts(theme, gallery.tenant as TenantDto);
    });
  }

  ngOnInit() {
    this.galleryService.onGalleryDeleted$.pipe(takeUntil(this.destroyed$)).subscribe(async gallery => {
      if (!gallery || gallery.id !== this.gallery()?.id) return;
      this.router.navigateByUrl(await this.getBackLink());
    });

    document.addEventListener('error', this.boundCheckImageError, true);
  }

  private async addEmptySectionIfNeeded() {
    if (!this.galleryPermissions().can_edit) return;
    const sections = this.sections();
    if (sections && !sections.length) {
      const x = this.storage.get<number>('add_initial_section_' + this.gallery().id);
      if (!x || Date.now() - x > 1000) {
        this.storage.set('add_initial_section_' + this.gallery().id, Date.now());
        try {
          await this.addSection('asset', 0, true);
        } catch (e) {
          console.log(e);
        }
      }
    }
  }

  async getBackLink() {
    const isTeamMember = this.isTeamMember();
    const gallery = this.gallery();
    if (!gallery) return;
    const folderId = gallery.folder as string;
    const folderIsAlsoShared =
      !isTeamMember && folderId
        ? (await firstValueFrom(this.folderService.getFolders())).some(f => f.id === folderId)
        : false;
    return getGalleryBackLink(gallery, isTeamMember, folderIsAlsoShared);
  }

  private findSectionsToMove(position: number): Section[] {
    const sectionsToMove = [];
    const current = this.sections().find(s => s.position === position);
    if (current) {
      sectionsToMove.push(current);
      return sectionsToMove.concat(this.findSectionsToMove(position + 1));
    } else {
      return sectionsToMove;
    }
  }

  async openAddSection(position: number) {
    const resp = await this.dialog.open(AddSectionDialogComponent, { gallery: this.gallery() });
    if (resp) this.addSection(<SectionObjectType>resp, position);
  }

  async addSection(type: SectionObjectType, position: number, initial = false): Promise<Section> {
    if (type === 'gallery' && !this.permissionService.canDisplayElement(billingScopes.gallery.subGallery)) {
      this.checkoutService.open(billingScopes.gallery.subGallery);
      return;
    }

    if (type === 'embed' && !this.permissionService.canDisplayElement(billingScopes.gallery.embedSection)) {
      this.checkoutService.open(billingScopes.gallery.embedSection);
      return;
    }

    const sectionsToMove = this.findSectionsToMove(position);
    this.analytics.track('add-section', false, { type });

    const [section] = await Promise.all([
      firstValueFrom(this.sectionService.createSection(this.gallery().id, type, position, initial)),
      ...sectionsToMove.map(section => {
        section.position += 1;
        return firstValueFrom(this.sectionService.updateSection(section, { position: section.position }));
      }),
    ]);
    this.check.detectChanges();
    if (initial) return section;
    await sleep(0);

    const el = document.getElementById(`section-${section.id}`);
    if (el) {
      const sectionPosition = document.scrollingElement.scrollTop + el.getBoundingClientRect().top;
      window.scrollTo({
        top: sectionPosition - 100,
        behavior: 'smooth',
      });
    }
    return section;
  }

  async addSectionWithAssets(files: File[], position: number) {
    const section = await this.addSection('asset', position);
    await sleep(100);
    this.assetSections.find(component => component.section().id === section.id).uploadAssets(files);
  }

  onSubGalleryMoveStart(data: { gallery: GalleryDto; section: EditAlbumSectionComponent; event: MouseEvent }) {
    this.draggingGalleryItem = data.gallery;
    this.currentDragSection = data.section;
    this.check.detectChanges();
  }

  onSubGalleryMove(event: MouseEvent) {
    const albumSectionHtmls: HTMLElement[] = this.albumSectionHtmls.map(i => i.nativeElement);

    this.stopScroll();
    if (event.clientY - 64 < dragScrollThreshold) {
      this.startScroll(-3);
    } else if (window.innerHeight - event.clientY < dragScrollThreshold) {
      this.startScroll(3);
    }

    if (!(this.currentDragSection instanceof EditAlbumSectionComponent)) return;

    const index = albumSectionHtmls.findIndex(el => {
      const bounds = el.getBoundingClientRect();
      const diff = event.clientY - bounds.top;
      return diff > 0 && diff < bounds.height;
    });

    if (index === -1) return;
    const section = this.albumSections.get(index);
    this.albumSections.forEach(s => s.hideInsertPosition());
    section.showInsertPositionBasedOnHover(Rect.fromMouseEvent(event));
    this.check.detectChanges();
  }

  async onSubGalleryMoveDrop(event: MouseEvent) {
    const albumSectionHtmls: HTMLElement[] = this.albumSectionHtmls.map(i => i.nativeElement);

    const index = albumSectionHtmls.findIndex(el => {
      const bounds = el.getBoundingClientRect();
      const diff = event.clientY - bounds.top;
      return diff > 0 && diff < bounds.height;
    });

    if (index > -1) {
      const newSection = this.albumSections.get(index);
      const oldSection = <EditAlbumSectionComponent>this.currentDragSection;
      oldSection.legacyToBeRemovedGalleries = oldSection.legacyToBeRemovedGalleries.slice();
      const oldGalleryIndex = oldSection.legacyToBeRemovedGalleries.findIndex(
        g => g.id === oldSection.draggingGallery().id
      );

      if (oldSection === newSection) {
        let newGalleryIndex = newSection.insertIndex;
        oldSection.legacyToBeRemovedGalleries.splice(oldGalleryIndex, 1);
        if (newGalleryIndex > oldGalleryIndex) {
          newGalleryIndex -= 1;
        }
        newSection.legacyToBeRemovedGalleries.splice(newGalleryIndex, 0, this.draggingGalleryItem);
      } else {
        oldSection.legacyToBeRemovedGalleries.splice(oldGalleryIndex, 1);
        newSection.legacyToBeRemovedGalleries.splice(newSection.insertIndex, 0, this.draggingGalleryItem);
      }

      this.draggingGalleryItem.section = newSection.section.id;
      this.galleryService.updateGallery(this.draggingGalleryItem.id, { section: newSection.section.id });
      this.check.detectChanges();
    }

    this.currentDragSection = undefined;
    this.draggingGalleryItem = undefined;
    this.albumSections.forEach(s => {
      s.hideInsertPosition();
      s.droppedGallery();
    });
    this.check.detectChanges();
  }

  async onMove(
    sectionId: string,
    event: {
      clientX: number;
      clientY: number;
      item: GalleryItem;
      inSection: boolean;
      sectionComponent: AssetsSectionComponent;
    }
  ) {
    const assetSectionHtmls: HTMLElement[] = this.assetSectionHtmls.map(i => i.nativeElement);
    this.stopScroll();
    if (event.clientY - 64 < dragScrollThreshold) {
      this.startScroll(-3);
    } else if (window.innerHeight - event.clientY < dragScrollThreshold) {
      this.startScroll(3);
    }

    if (this.startDragSection === undefined) {
      this.startDragSection = event.sectionComponent;
      // eslint-disable-next-line no-debugger
      if (!this.startDragSection.draggingItem) debugger;
      this.startDragPosition = this.startDragSection.draggingItem.asset.position;
      this.draggingItem = event.item;
    }
    this.currentDragSection = event.sectionComponent;

    const oldSection = this.assetSections.find(section => section.section().id === sectionId);
    this.currentDragGallery = undefined;
    if (oldSection) oldSection.draggingItemDropOnSubGallery = false;
    this.check.detectChanges();

    if (event.inSection) {
      if (this.cover.coverPreviewAsset()) this.cover.setPreview(undefined);
      return;
    }
    const index = assetSectionHtmls.findIndex(el => {
      const bounds = el.getBoundingClientRect();
      const diff = event.clientY - bounds.top;
      return diff > 0 && diff < bounds.height;
    });

    if (index > -1) {
      if (this.cover.coverPreviewAsset()) this.cover.setPreview(undefined);
      const item = oldSection.draggingItem;
      if (!item) return;

      const newSection = this.assetSections.get(index);
      if (!newSection) throw 'Coudnt find drop section';
      if (!newSection.items) throw 'Coudnt find items in drop section';
      if (newSection.section().id === oldSection.section().id) return;

      oldSection.draggingItem = undefined;
      oldSection.potentialDraggingItem = undefined;

      if (oldSection.items.length === 1) {
        oldSection.forceAssetsView = true;
      }

      // updating the asset with the new section
      item.asset.position = newSection.items.length;
      item.asset.section = newSection.section().id;

      // Removing item from old section
      const oldIndex = oldSection.items.indexOf(item);
      oldSection.items.splice(oldIndex, 1);

      // Adding item to new section
      newSection.items.push(item);

      // Transferring dragged asset from old section to new one
      newSection.mousePosOffsetX = oldSection.mousePosOffsetX;
      newSection.mousePosOffsetY = oldSection.mousePosOffsetY;
      newSection.mousePosX = oldSection.mousePosX;
      newSection.mousePosY = oldSection.mousePosY;
      newSection.draggingItem = item;
      await sleep(50);
      newSection.handleItemMove(event.clientX, event.clientY);
    } else {
      const coverBox = this.coverEl.nativeElement.getBoundingClientRect();
      if (event.clientY > coverBox.top && event.clientY < coverBox.top + coverBox.height) {
        this.cover.setPreview(event.item.asset);
      } else {
        if (this.cover.coverPreviewAsset()) {
          this.cover.setPreview(undefined);
        }

        const albumSectionHtmls: HTMLElement[] = this.albumSectionHtmls.map(i => i.nativeElement);
        const index = albumSectionHtmls.findIndex(el => {
          const bounds = el.getBoundingClientRect();
          const diff = event.clientY - bounds.top;
          return diff > 0 && diff < bounds.height;
        });

        const hoverAlbumSection = this.albumSections.get(index);
        if (hoverAlbumSection) {
          hoverAlbumSection.getGalleryBounds().subscribe(galleryBounds => {
            const galleryIndex = galleryBounds.findIndex(bounds => {
              if (event.clientX < bounds.left) return;
              if (event.clientX > bounds.left + bounds.width) return;
              if (event.clientY < bounds.top) return;
              if (event.clientY > bounds.top + bounds.height) return;
              return true;
            });
            if (galleryIndex > -1) {
              hoverAlbumSection.galleries$.pipe(take(1)).subscribe(galleries => {
                this.currentDragGallery = galleries[galleryIndex];
                oldSection.draggingItemDropOnSubGallery = true;
                this.check.detectChanges();
              });
            }
          });
        }
      }
    }
  }

  async onDrop(didChange: boolean) {
    if (!this.draggingItem) return;
    this.stopScroll();
    if (this.cover.coverPreviewAsset()) {
      this.cover.convertAssetToCover();
      this.startDragSection.draggingItem = undefined;
      this.cover.setPreview(undefined);
      this.check.detectChanges();
    }
    if (this.currentDragGallery) {
      const section = this.currentDragSection as AssetsSectionComponent;
      const respPromise = this.moveAsset(this.draggingItem.asset, this.currentDragGallery);
      const item = section.onMovedOutInitial(this.draggingItem.asset);
      const resp = await respPromise;
      if (resp === true) section.onMovedOutConfirm();
      else section.onMovedOutCancel(item);

      this.assetSections.map(section => {
        section.forceAssetsView = false;
        section.noGalleryAnimation = false;
        this.check.detectChanges();
      });
    } else {
      const currentSection = this.currentDragSection as AssetsSectionComponent;

      const sectionValue = this.startDragSection.section();
      const sectionVal = currentSection.section();
      if (!didChange && this.startDragSection && sectionValue.id !== sectionVal.id) {
        didChange = true;
      }
      if (didChange) {
        if (!this.cover.coverPreviewAsset()) {
          // Going to move an asset and change one/two sections into manual sorting

          const affectedSections = [currentSection];
          if (this.startDragSection && sectionVal.id !== sectionVal.id) {
            affectedSections.push(this.startDragSection);
          }

          if (currentSection.sorting !== 'manual') {
            const assetCount = currentSection.items.length;

            if (assetCount >= 100) {
              const resp = await this.dialog.open(ManualConfirmDialogComponent);
              if (!resp) {
                // Undo move
                this.draggingItem = undefined;
                this.startDragSection = undefined;
                this.startDragPosition = undefined;
                affectedSections.forEach(section => {
                  section.sortItemsFromExternal();
                  section.calculateAllItemPositions();
                });
                this.check.detectChanges();
                return;
              }
            }
          }

          currentSection.changeAssetSortingToManual();

          const params = { section: <string>this.draggingItem.asset.section };
          this.assetService.updateAsset(
            this.draggingItem.asset,
            params,
            this.gallery().id,
            <string>this.draggingItem.asset.section,
            false,
            true
          );
          this.check.detectChanges();
        }

        await Promise.all(
          this.assetSections.map(async section => {
            section.noGalleryAnimation = true;
            this.check.detectChanges();
            await section.handleAssetOrderChange();
            section.forceAssetsView = false;
            section.noGalleryAnimation = false;
            this.check.detectChanges();
          })
        );
      }
    }
    this.draggingItem = undefined;
    this.startDragSection = undefined;
    this.startDragPosition = undefined;
    this.check.detectChanges();
  }

  onCoverChange() {
    this.cover.ngOnChanges({});
  }

  startScroll(dir: number) {
    this.scrollingPage = dir;
    this.scroll(dir);
  }

  private async scroll(dir: number) {
    if (this.scrollingPage !== dir) return;
    document.scrollingElement.scrollTop += dir;
    await sleep(20);
    this.scroll(dir);
  }

  stopScroll() {
    this.scrollingPage = 0;
  }

  async moveAsset(asset: AssetDto, gallery: GalleryDto) {
    return await this.assetMoveService.moveAssets([asset], gallery);
  }

  async toggleColorPicker() {
    if (!this.colorPickerOpen) {
      await sleep(0);
      this.colorPickerOpen = true;
      this.check.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    document.removeEventListener('error', this.boundCheckImageError, true);
  }
}
